import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import RenderHTML, { defaultSystemFonts } from 'react-native-render-html';

import LS from '../../../utils/localStorageUtils';
import { ACHIEVEMENTS_FONT as FONT, PANEL_WIDTH } from '../Achievements/achievementsTheme';
import { RedesignScrollView } from '../common/RedesignScrollView';
import RedesignModal from './RedesignModal';
import {
	createPopupHtmlStyles,
	POPUP,
	POPUP_TINT,
	popupBodyStyle,
	popupColors,
	PopupDivider,
	PopupHeader,
	PopupToggle,
} from './RedesignPopup';
import { useRedesignScale } from './useRedesignScale';

interface PersonalDataModalProps {
	onClose: () => void;
}

const PERSONAL_DATA_PREFERENCES_KEY = 'personalDataPreferences';

/**
 * Personal data control, opened from the Settings popup's "Personal data" row. Drawn as a sibling
 * of "Modal / Settings" (5054:78827): the same panel and header, the consents as its surface/raised
 * rows with the green toggle on the right, and the notes in the popups' secondary copy. The body
 * scrolls, so on a short screen the panel simply gets shorter.
 */
const PANEL_HEIGHT = 600;
const ROW = { gap: 8, radius: 10, paddingX: 14, paddingY: 10, innerGap: 12 };
/** Room the scroll body keeps on its right for the 4-wide scroll bar. */
const SCROLL_GUTTER = 14;

const PersonalDataModal = ({ onClose }: PersonalDataModalProps) => {
	const [marketing, setMarketing] = useState(true);
	const [offers, setOffers] = useState(true);
	const [terms, setTerms] = useState(true);
	const [isHydrated, setIsHydrated] = useState(false);

	const { t } = useTranslation();
	const scale = useRedesignScale();
	const styles = useMemo(() => createStyles(scale), [scale]);
	const tagsStyles = useMemo(() => createPopupHtmlStyles(scale), [scale]);
	const systemFonts = useMemo(() => [...defaultSystemFonts, FONT], []);

	useEffect(() => {
		let isMounted = true;

		const loadPreferences = async () => {
			const storedPreferences = await LS.getAsync(PERSONAL_DATA_PREFERENCES_KEY);

			if (!isMounted) {
				return;
			}

			if (!storedPreferences) {
				setIsHydrated(true);
				return;
			}

			try {
				const parsedPreferences = JSON.parse(storedPreferences) as {
					marketing?: boolean;
					offers?: boolean;
					terms?: boolean;
				};

				setMarketing(parsedPreferences.marketing ?? true);
				setOffers(parsedPreferences.offers ?? true);
				setTerms(parsedPreferences.terms ?? true);
			} catch {
				LS.remove(PERSONAL_DATA_PREFERENCES_KEY);
			}

			if (isMounted) {
				setIsHydrated(true);
			}
		};

		loadPreferences().catch(() => {
			if (isMounted) {
				setIsHydrated(true);
			}
		});

		return () => {
			isMounted = false;
		};
	}, []);

	useEffect(() => {
		if (!isHydrated) {
			return;
		}

		LS.set(
			PERSONAL_DATA_PREFERENCES_KEY,
			JSON.stringify({
				marketing,
				offers,
				terms,
			}),
		);
	}, [isHydrated, marketing, offers, terms]);

	// The data-copy line is server HTML with its own privacy-policy link.
	const htmlWidth = (PANEL_WIDTH - 2 * POPUP.paddingX - SCROLL_GUTTER) * scale;

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			panel={{ height: PANEL_HEIGHT, radius: POPUP.radius, tint: POPUP_TINT, overScreen: true }}
		>
			<View style={styles.body}>
				<PopupHeader
					scale={scale}
					title={t('T_SITE_MENU_PERSONAL_DATA')}
					onClose={onClose}
					closeLabel={t('T_SITE_A11Y_CLOSE_MODAL', { defaultValue: 'Close modal' })}
				/>

				<RedesignScrollView
					contentContainerStyle={styles.content}
					indicatorInsets={{ right: 0, top: 0, bottom: 0 }}
				>
					<Text style={styles.text}>{t('T_SITE_DATA_CONTROL_SUBTITLE')}</Text>

					<View style={styles.rows}>
						<ConsentRow styles={styles} scale={scale} value={marketing} onChange={setMarketing}>
							{t('T_SITE_DATA_CONTROL_MARKETING_INFO')}
						</ConsentRow>
						<ConsentRow styles={styles} scale={scale} value={offers} onChange={setOffers}>
							{t('T_SITE_DATA_CONTROL_TARGETING_INFO')}
						</ConsentRow>
						<ConsentRow styles={styles} scale={scale} value={terms} onChange={setTerms}>
							<Text style={styles.link}>{t('T_SITE_DATA_CONTROL_TERMS_LINK')}</Text>
							<Text> {t('T_SITE_DATA_CONTROL_TERMS_AND')} </Text>
							<Text style={styles.link}>{t('T_SITE_DATA_CONTROL_PRIVACY_LINK')}</Text>
							<Text style={styles.text}> {t('T_SITE_DATA_CONTROL_TERMS_INFO')}</Text>
						</ConsentRow>
					</View>

					<PopupDivider scale={scale} />

					<RenderHTML
						contentWidth={htmlWidth}
						source={{ html: `<p>${t('T_SITE_DATA_CONTROL_DATA_COPY')}</p>` }}
						systemFonts={systemFonts}
						tagsStyles={tagsStyles}
						baseStyle={styles.text}
					/>
				</RedesignScrollView>
			</View>
		</RedesignModal>
	);
};

type Styles = ReturnType<typeof createStyles>;

/** A consent: its wording on the left of a surface/raised row, the Settings toggle on the right. */
const ConsentRow = ({
	styles,
	scale,
	value,
	onChange,
	children,
}: {
	styles: Styles;
	scale: number;
	value: boolean;
	onChange: (next: boolean) => void;
	children: React.ReactNode;
}) => (
	<View style={styles.row}>
		<Text style={styles.label}>{children}</Text>
		<PopupToggle scale={scale} value={value} onChange={() => onChange(!value)} />
	</View>
);

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	return StyleSheet.create({
		body: popupBodyStyle(scale),
		content: {
			gap: s(12),
			paddingRight: s(SCROLL_GUTTER),
			paddingBottom: s(4),
		},
		text: {
			fontFamily: FONT,
			fontWeight: '500',
			fontSize: s(14),
			lineHeight: s(20),
			color: popupColors.secondary,
		},
		rows: {
			gap: s(ROW.gap),
		},
		row: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(ROW.innerGap),
			paddingHorizontal: s(ROW.paddingX),
			paddingVertical: s(ROW.paddingY),
			borderRadius: s(ROW.radius),
			borderWidth: Math.max(1, scale),
			borderColor: popupColors.plateBorder,
			backgroundColor: popupColors.raised,
		},
		label: {
			flex: 1,
			fontFamily: FONT,
			fontWeight: '600',
			fontSize: s(14),
			lineHeight: s(19),
			color: popupColors.gold50,
		},
		link: {
			color: popupColors.gold200,
			textDecorationLine: 'underline',
		},
	});
};

export default PersonalDataModal;
